import React, { useEffect, useState } from "react";
import "react-h5-audio-player/lib/styles.css";
import AudioPlayer from "react-h5-audio-player";
import "./App.css";

const NOW_PLAYING_POLL_INTERVAL_MS = 20000;

const fetchTrackFromXmlFeed = async (metadataUrl, signal) => {
  const response = await fetch(metadataUrl, {
    cache: "no-store",
    signal,
  });

  if (!response.ok) {
    throw new Error(`Metadata request failed with ${response.status}`);
  }

  const xmlText = await response.text();
  const xml = new window.DOMParser().parseFromString(xmlText, "application/xml");

  if (xml.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Unable to parse metadata feed");
  }

  const artistNode = xml.getElementsByTagName("artist")[0];
  const titleNode = xml.getElementsByTagName("title")[0];
  const artist = artistNode?.textContent?.trim() || "";
  const title = titleNode?.textContent?.trim() || "";

  if (artist && title) {
    return `${artist} - ${title}`;
  }

  return title || artist;
};

const fetchTrackFromJsonFeed = async (metadataUrl, signal) => {
  const response = await fetch(metadataUrl, {
    cache: "no-store",
    signal,
  });

  if (!response.ok) {
    throw new Error(`Metadata request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (payload.track) {
    return String(payload.track).trim();
  }

  if (payload.artist && payload.title) {
    const artist = String(payload.artist).trim();
    const title = String(payload.title).trim();
    if (artist && title) {
      return `${artist} - ${title}`;
    }
    return title || artist;
  }

  const firstSong = Array.isArray(payload.songs) ? payload.songs[0] : null;
  if (firstSong) {
    const artist = (firstSong.artist || "").trim();
    const title = (firstSong.song || firstSong.title || "").trim();
    if (artist && title) {
      return `${artist} - ${title}`;
    }
    return title || artist;
  }

  return "";
};

const fetchTrackFromTextFeed = async (metadataUrl, signal) => {
  const response = await fetch(metadataUrl, {
    cache: "no-store",
    signal,
  });
  if (!response.ok) {
    throw new Error(`Metadata request failed with ${response.status}`);
  }
  return (await response.text()).trim();
};

const STATIONS = [
  {
    id: 1,
    name: "101.5 K-Rock",
    description: "Mainstream rock anthems with a sharp Manhattan edge.",
    image: "/stations/krock-1015.jpg",
    url: "https://stream-01.surfernetwork.com/drscz5r6naxuv?zt=eyJhbGciOiJIUzI1NiJ9.eyJzdHJlYW0iOiJkcnNjejVyNm5heHV2IiwiaG9zdCI6InN0cmVhbS0wMS5zdXJmZXJuZXR3b3JrLmNvbSIsInRtIjpmYWxzZSwicnR0bCI6NSwianRpIjoiejRVdTlWSGlRcHloZ3lLZlNiMDZGQSIsImlhdCI6MTc3NTkzNTc1NiwiZXhwIjoxNzc1OTM1ODE2fQ.xtM3hBBV3OJdYc1ITgSnPVX_bEnHy1SVtRmwF-7uQ3A",
    nowPlayingUrl: "/api/metadata/surfer/drscz5r6naxuv",
    nowPlayingFormat: "json",
  },
  {
    id: 2,
    name: "90.9 The Bridge",
    description: "Indie and alt cuts from Kansas City with a gritty streak.",
    image: "/stations/thebridge-909.jpg",
    url: "https://prod-13-222-197-5.amperwave.net/publictv19-ktbgfmaac-hlsc1.m3u8/?z=4aaa46288d4f48218b7c9f56b17116fb&p=1",
    nowPlayingUrl: "/api/metadata/bridge",
    nowPlayingFormat: "json",
  },
  {
    id: 3,
    name: "106.7 KROQ",
    description: "Pasadena alt-rock staples with fresh tracks and late-night bite.",
    image: "/stations/kroq-1067.jpg",
    url: "https://prod-18-234-58-253.amperwave.net/audacy-kroqfmaac-hlsc.m3u8",
    nowPlayingUrl: "/api/metadata/m3u8?url=https%3A%2F%2Fprod-18-234-58-253.amperwave.net%2Faudacy-kroqfmaac-hlsc.m3u8",
    nowPlayingFormat: "json",
  },
  {
    id: 5,
    name: "91.9 Wildcat",
    description: "The longest-continuously running college FM station in the nation.",
    image: "/stations/wildcat919.jpg",
    url: "https://s.radiowave.io/ksdb.mp3"
  },
  {
    id: 11,
    name: "96.3 X96",
    description: "Utah's Original Alternative for over 30 years along the Wasatch Front with Radio From Hell in the mornings",
    image: "/stations/x96.jpg",
    url: "https://das-edge59-sa48-futuri-lax02.cdnstream.com/7346_48k.aac",
    nowPlayingUrl: "https://yp.cdnstream1.com/metadata/7346_48k/last/15.json",
    nowPlayingFormat: "json",
  },
  {
    id: 12,
    name: "102.7 EQX",
    description: "EQX is live, local, independent, alternative radio, originating from Manchester",
    image: "/stations/weqx1027.jpg",
    url: "https://stream-01.surfernetwork.com/cc6a319f460vv?zt=eyJhbGciOiJIUzI1NiJ9.eyJzdHJlYW0iOiJjYzZhMzE5ZjQ2MHZ2IiwiaG9zdCI6InN0cmVhbS0wMS5zdXJmZXJuZXR3b3JrLmNvbSIsInRtIjpmYWxzZSwicnR0bCI6NSwianRpIjoiNnFQdXJtX21UVHV5R0R0UWE2WURKQSIsImlhdCI6MTc3NTk2MjA1NCwiZXhwIjoxNzc1OTYyMTE0fQ.Bxw9fo--KCaGBvOrTG9StPZbENblXkRyTN7hFPsXNu0",
    nowPlayingUrl: "/api/metadata/surfer/cc6a319f460vv",
    nowPlayingFormat: "json",
  },
  {
    id: 13,
    name: "105.9 The Rock",
    description: "Nashville's Only Classic Rock",
    image: "stations/therock1059.jpg",
    url: "https://n18a-e2.revma.ihrhls.com/zc2780/24_12qlhwezogl9602/playlist.m3u8?streamid=2780&zip=&aw_0_1st.playerid=iHeartRadioWebPlayer&aw_0_1st.skey=12299580791&clientType=web&companionAds=false&deviceName=web-mobile&dist=iheart&host=webapp.US&listenerId=1b87436d18d8bfeb5df665627213d8ee&playedFrom=157&pname=live_profile&profileId=12299580791&stationid=2780&terminalId=159&territory=US",
    nowPlayingUrl: "/api/metadata/iheart/2780",
    nowPlayingFormat: "json",
  },
  {
    id: 14,
    name: "92.3 K-Rock 90's",
    description: "92.3 K-Rock 90's brings back the '90s spirit with a mix of Rock and Alternative hits",
    image: "/stations/krock923.jpg",
    url: "https://streaming.live365.com/a76295/playlist.m3u8",
    nowPlayingUrl: "/api/metadata/live365/a76295",
    nowPlayingFormat: "json",
  },
  {
    id: 15,
    name: "95.5 Rock",
    description: "This is Smoky Hills Rock 95.5. From McPherson to Salina, we rock the Smoky Hills!",
    image: "/stations/rock955.jpg",
    url: "https://us2.maindigitalstream.com/ssl/KLBQ",
    nowPlayingUrl: "/api/metadata/iheart/9769",
    nowPlayingFormat: "json",
  },
  {
    id: 16,
    name: "1150 KSAL NewsRadio",
    description: "KSAL is Salina's heritage news, sports, and talk radio station.",
    url: "https://ice23.securenetsystems.net/KSALAM",
    image: "/stations/ksal1150.png",
  },
  {
    id: 17,
    name: "104.9 KSAL",
    description: "FM 104.9 - KSAL-FM, Your Home for the 104 Minute Music Sweep!",
    url: "https://ice23.securenetsystems.net/KSALFM",
    image: "/stations/ksal1049.png",
    nowPlayingUrl: "/api/metadata/securenet/KSALFM",
    nowPlayingFormat: "json",
  },
  {
    id: 19,
    name: "98.9 The Rock",
    description: "Kansas City's Rock Station. KQRC-FM is spinning the best Rock, live from Kansas City.",
    image: "/stations/rock989.jpg",
    url: "https://prod-18-234-58-253.amperwave.net/audacy-kqrcfmaac-hlsc.m3u8/",
    nowPlayingUrl: "/api/metadata/audacy/101-919",
    nowPlayingFormat: "json",
  },
  {
    id: 4,
    name: "SomaFM Indie Pop Rocks!",
    description: "Indie-pop and alt-rock cuts with a bright West Coast edge.",
    image: "stations/somaindiepoprocks.jpg",
    url: "https://ice6.somafm.com/indiepop-128-aac",
    nowPlayingUrl: "https://somafm.com/recent/indiepop.tre.xml",
  },
  {
    id: 6,
    name: "Radio Paradise Rock Mix",
    description: "Eclectic rock sequencing with deeper cuts and polished flow.",
    image: "stations/radioparadiserockmix.jpg",
    url: "https://stream.radioparadise.com/rock-flac",
    nowPlayingUrl: "/api/metadata/radioparadise?chan=2",
    nowPlayingFormat: "json",
  },
  {
    id: 8,
    name: "Megarock Radio",
    description: "Hard rock and classic metal with a bigger, louder feel.",
    image: "stations/megarockradio.jpg",
    url: "https://stream3.megarockradio.net:80/",
    nowPlayingUrl: "/api/metadata/megarock",
    nowPlayingFormat: "text",
  },
  {
    id: 9,
    name: "The Independent FM",
    description: "Modern indie-rock rotation with a sharper club-night tone.",
    image: "stations/theindependentfm.jpg",
    url: "https://listen.radioking.com/radio/293701/stream/340084",
    nowPlayingUrl: "https://api.radioking.io/widget/radio/the-independent-fm-1/track/current",
    nowPlayingFormat: "json",
  },
  {
    id: 10,
    name: "Radio Rock On",
    description: "Straight-up classic rock riffs with a gritty throwback pulse.",
    image: "stations/radiorockon.png",
    url: "https://streaming.radiostreamlive.com/radiorockon_devices",
  }
];

function App() {
  // Load last station from localStorage if available
  const [currentStation, setCurrentStation] = useState(() => {
    const lastId = localStorage.getItem("lastStationId");
    if (lastId) {
      const found = STATIONS.find((s) => String(s.id) === lastId);
      if (found) return found;
    }
    return STATIONS[0];
  });
  const [search, setSearch] = useState("");

  const [theme, setTheme] = useState("light");

  // if the audio player is actively playing
  const [isPlaying, setIsPlaying] = useState(false);
  const [nowPlayingTrack, setNowPlayingTrack] = useState("");
  const [nowPlayingStatus, setNowPlayingStatus] = useState("idle");

  // On mount: load theme from localStorage or system preference
  useEffect(() => {
    const stored = window.localStorage.getItem("catsoup-radio-theme");
    if (stored === "light" || stored === "dark") {
      setTheme(stored);
      document.documentElement.setAttribute("data-theme", stored);
      return;
    }

    const prefersDark =
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
    const initial = prefersDark ? "dark" : "light";
    setTheme(initial);
    document.documentElement.setAttribute("data-theme", initial);
  }, []);

  useEffect(() => {
    setNowPlayingTrack("");

    if (!currentStation || !isPlaying) {
      setNowPlayingStatus("idle");
      return undefined;
    }

    if (!currentStation.nowPlayingUrl) {
      setNowPlayingStatus("unsupported");
      return undefined;
    }

    let isActive = true;
    const controller = new window.AbortController();

    const pollNowPlaying = async () => {
      setNowPlayingStatus((previous) =>
          previous === "ready" ? "ready" : "loading"
      );

      try {
        const track = await (async () => {
          if (currentStation.nowPlayingFormat === "json") {
            return fetchTrackFromJsonFeed(currentStation.nowPlayingUrl, controller.signal);
          }
          if (currentStation.nowPlayingFormat === "text") {
            return fetchTrackFromTextFeed(currentStation.nowPlayingUrl, controller.signal);
          }
          return fetchTrackFromXmlFeed(currentStation.nowPlayingUrl, controller.signal);
        })();

        if (!isActive) {
          return;
        }

        if (track) {
          setNowPlayingTrack(track);
          setNowPlayingStatus("ready");
        } else {
          setNowPlayingStatus("unsupported");
        }
      } catch (error) {
        if (!isActive || error.name === "AbortError") {
          return;
        }

        setNowPlayingStatus("error");
      }
    };

    pollNowPlaying();
    const timerId = window.setInterval(pollNowPlaying, NOW_PLAYING_POLL_INTERVAL_MS);

    return () => {
      isActive = false;
      controller.abort();
      window.clearInterval(timerId);
    };
  }, [currentStation, isPlaying]);

  // Persist currentStation to localStorage
  useEffect(() => {
    if (currentStation && currentStation.id) {
      localStorage.setItem("lastStationId", String(currentStation.id));
    }
  }, [currentStation]);

  useEffect(() => {
    if (!isPlaying || !currentStation) {
      if (navigator.mediaSession) {
        navigator.mediaSession.metadata = null;
      }
      return;
    }

    // Compose metadata
    const title = nowPlayingTrack || currentStation.name;
    const artist = nowPlayingTrack && nowPlayingTrack.includes(' - ')
      ? nowPlayingTrack.split(' - ')[0]
      : currentStation.name;
    const trackTitle = nowPlayingTrack && nowPlayingTrack.includes(' - ')
      ? nowPlayingTrack.split(' - ')[1]
      : nowPlayingTrack || currentStation.name;
    // Make artwork URLs absolute for iOS compatibility
    let artwork = undefined;
    if (currentStation.image) {
      let imgUrl = currentStation.image;
      if (!/^https?:\/\//.test(imgUrl)) {
        // Relative path: make absolute
        imgUrl = (window.location.origin || '') + (imgUrl.startsWith('/') ? imgUrl : '/' + imgUrl);
      }
      artwork = [
        { src: imgUrl, sizes: '512x512', type: 'image/png' },
        { src: imgUrl, sizes: '192x192', type: 'image/png' },
      ];
    }

    if (navigator.mediaSession) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: trackTitle,
        artist: artist,
        album: currentStation.name,
        artwork: artwork,
      });
    }
  }, [isPlaying, currentStation, nowPlayingTrack]);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    window.localStorage.setItem("catsoup-radio-theme", next);
  };

  const filteredStations = STATIONS.filter((s) =>
      (s.name + " " + s.description)
          .toLowerCase()
          .includes(search.toLowerCase())
  );

  return (
      <div className="app-root">
        <div className="app-shell">
          {/* Header */}
          <header className="app-header">
            <div className="app-header-left">
              <h1 className="app-title">
                <img
                  src="/icons/icon-48x48.png"
                  alt=""
                  aria-hidden="true"
                  className="app-title-logo"
                />
                Catsoup Radio
              </h1>
              <p className="app-subtitle">
                A little home‑made radio, just for you.
              </p>
            </div>

            <div className="app-header-right">
              {/* Theme toggle */}
              <button
                  type="button"
                  className="theme-toggle"
                  onClick={toggleTheme}
                  role="switch"
                  aria-checked={theme === "dark"}
                  aria-label="Toggle light/dark theme"
              >
                <span className="theme-toggle-track" aria-hidden="true">
                  <span
                      className={
                          "theme-toggle-thumb" +
                          (theme === "dark" ? " theme-toggle-thumb--right" : "")
                      }
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {theme === "dark" ? "dark_mode" : "light_mode"}
                    </span>
                  </span>
                </span>
                <span className="theme-toggle-label">
                {theme === "dark" ? "Dark" : "Light"}
              </span>
              </button>
            </div>
          </header>

          {/* Main content */}
          <main className="app-main">
            {/* Stations list */}
            <section className="stations-panel">
              <div className="panel-header">
                <h2 className="panel-title">Stations</h2>
                <span className="panel-count">
                {filteredStations.length} of {STATIONS.length}
              </span>
              </div>

              <div className="search-wrapper">
                <input
                    type="text"
                    placeholder="Search for a station…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <span
                    className="search-icon material-symbols-outlined"
                    aria-hidden="true"
                >
                  search
                </span>
              </div>

              <div className="stations-list">
                {filteredStations.map((station) => {
                  const isActive =
                      currentStation && currentStation.id === station.id;
                  return (
                      <button
                          key={station.id}
                          type="button"
                          className={
                              "station-card" +
                              (isActive ? " station-card--active" : "")
                          }
                          onClick={() => setCurrentStation(station)}
                      >
                        <div className="station-card-inner">
                          {/* Thumbnail */}
                          <div className="station-thumb-wrapper">
                            {station.image ? (
                                <img
                                    src={station.image}
                                    alt={station.name}
                                    className="station-thumb"
                                />
                            ) : (
                                <div
                                    className="station-thumb-fallback material-symbols-outlined"
                                    aria-hidden="true"
                                >
                                  headphones
                                </div>
                            )}
                          </div>

                          {/* Text content */}
                          <div className="station-card-content">
                            <div className="station-card-header">
                              <span className="station-name">
                                {station.name}
                              </span>
                              {isActive && isPlaying && (
                                  <span className="station-badge" aria-label="Now playing" title="Now playing">
                                    <span className="station-badge-bar" />
                                    <span className="station-badge-bar" />
                                    <span className="station-badge-bar" />
                                    <span className="station-badge-bar" />
                                  </span>
                              )}
                            </div>

                            <p className="station-description">
                              {station.description}
                            </p>
                          </div>
                        </div>
                      </button>
                  );
                })}
                {filteredStations.length === 0 && (
                    <p className="empty-message">
                      Nothing here yet. Try another word?
                    </p>
                )}
              </div>
            </section>

            {/* Player section */}
            <section className="player-panel">
              <div className="panel-header">
                <div>
                  <h2 className="panel-title">Now playing</h2>
                </div>
              </div>

              <div className="player-panel-body">
              {currentStation ? (
                  <>
                    <div className="now-playing-card">
                      <div className="artwork-placeholder">
                        {currentStation.image ? (
                            <img
                                src={currentStation.image}
                                alt={currentStation.name}
                                className="artwork-image"
                            />
                        ) : (
                            <span className="artwork-fallback-icons" aria-hidden="true">
                              <span className="material-symbols-outlined">
                                headphones
                              </span>
                              <span className="material-symbols-outlined">
                                pets
                              </span>
                            </span>
                        )}
                      </div>
                      <div className="now-playing-info">
                        <div className="now-playing-header">
                          <h3 className="now-playing-title">
                            {currentStation.name}
                          </h3>
                          {isPlaying && (
                              <span className="live-pill">Live</span>
                          )}
                        </div>
                        <p className="now-playing-description">
                          {nowPlayingStatus === "ready" ? nowPlayingTrack : ""}
                        </p>
                      </div>
                    </div>

                    <div className="player-card">
                      <AudioPlayer
                          src={currentStation.url}
                          autoPlay
                          showJumpControls={false}
                          customProgressBarSection={['MAIN_CONTROLS','CURRENT_TIME']}
                          customControlsSection={['VOLUME_CONTROLS']}
                          customIcons={{
                            volume: (
                              <span className="material-symbols-outlined rhap-material-icon" aria-hidden="true">
                                volume_up
                              </span>
                            ),
                            volumeMute: (
                              <span className="material-symbols-outlined rhap-material-icon" aria-hidden="true">
                                volume_off
                              </span>
                            ),
                          }}
                          layout="horizontal"
                          className="modern-audio-player"
                          customAdditionalControls={[]}
                          onPlay={() => setIsPlaying(true)}
                          onPause={() => setIsPlaying(false)}
                          onEnded={() => setIsPlaying(false)}
                      />
                    </div>
                  </>
              ) : (
                  <p className="empty-message">
                    Choose a station to start listening.
                  </p>
              )}
              </div>
            </section>
          </main>
        </div>
      </div>
  );
}

export default App;


